import { cloneDeep } from 'lodash';
import React, { useContext, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { Button } from '@progress/kendo-react-buttons';
import { useNavigate, useParams } from 'react-router-dom';
import { ProgressBar } from '@progress/kendo-react-progressbars';

import { putToStore } from '../../utils/indexedDB';
import { Stepper } from '../../components/stepper';
import { PlayerContext } from '../../contexts/player';
import { useGameTheme } from '../../hooks/use-game-theme';
import { ValidateGameCodeAsync } from '../../services/games';
import { GameDocumentContext } from '../../contexts/game-document';
import { UpdatePlayerStateAsync } from '../../utils/game-engine/base';
import { ReactComponent as Logo } from '../../assets/cg-icon-globe.svg';
import { DisplayLanguageContext } from '../../contexts/display-languages';
import { generateTitleById } from '../../utils/game-document/display-languages';
import { convertResourcesToBlobs } from '../../utils/game-document/assets/convert-resource-to-blob';

export const PreloadAssetPage = () => {
  const { gameCode } = useParams();
  const navigate = useNavigate();
  const [, setPlayer] = useContext(PlayerContext);
  const [gameDocumentstate, setGameDocumentState] =
    useContext(GameDocumentContext);
  const [displayLanguageContext] = useContext(DisplayLanguageContext);

  const [progress, setProgress] = React.useState({
    current: 0,
    total: 0
  });
  const [isDone, setIsDone] = useState<boolean>(false);

  const gameTheme = useGameTheme(gameCode ?? '');

  /**
   * @description handle next button after the preload assets is done
   * @returns 'Navigate to games/:gamecode
   */
  const handleNext = async () => {
    let gameCode = gameDocumentstate.gameCode;
    if (gameCode) {
      let codeIsValid = await ValidateGameCodeAsync(gameCode);
      if (codeIsValid) {
        navigate(`/games/${gameCode}`);
      }
    }
  };

  React.useEffect(() => {
    if (!gameDocumentstate.gameDocument) return;

    (async () => {
      const updated = await convertResourcesToBlobs(
        gameDocumentstate.gameDocument!,
        (c, t) => setProgress({ current: c, total: t }),
        () => {
          setIsDone(true);
          setPlayer((prev) =>
            UpdatePlayerStateAsync(prev, {
              ...prev.playerState!,
              isPreloadCompleted: true
            })
          );
        }
      );

      setGameDocumentState((prev) => {
        const result = cloneDeep(prev);
        result.gameDocument = updated;
        putToStore('game-document', result);
        return result;
      });
    })();
  }, []);

  return (
    <Container>
      <Row className={'align-items-center'}>
        <Col />
        <Col className={'text-center'}>
          {gameTheme.data?.logoUrl ? (
            <>
              <img
                src={gameTheme.data.logoUrl}
                style={{
                  width: '64px',
                  height: '64px'
                }}
                className={'event-image rounded-circle'}
              />
            </>
          ) : (
            <Logo style={{ width: 64 }} />
          )}
        </Col>
        <Col />
      </Row>
      <Row className={'gy-4 mt-4'}>
        <Col xs={12} className={'text-center'}>
          <h1>
            {generateTitleById(
              'f7c9251f-9bba-47c8-a554-7ddc95cd0dd4',
              gameDocumentstate,
              displayLanguageContext.displayLanguageSelected.resources!,
              'game'
            ) || 'Preload assets'}
          </h1>
        </Col>
        <Col xs={12}>
          <Stepper stepCount={4} currentStep={1} />
        </Col>
        <Col xs={12} className={'text-center'}>
          <strong>
            {generateTitleById(
              'd705595a-5864-4eb7-aa72-af7b2f2bb19e',
              gameDocumentstate,
              displayLanguageContext.displayLanguageSelected.resources!,
              'game'
            ) || 'It will take a few seconds'}{' '}
          </strong>
        </Col>
        <Col xs={12}>
          <ProgressBar
            labelVisible={false}
            value={progress.current}
            max={progress.total}
            progressStyle={{
              background: 'linear-gradient(127deg, #14bf8b 0%, #4698cb 100%)'
            }}
          />
          <p className={'text-center mb-0 fw-semibold'}>
            {(
              generateTitleById(
                '5d7d9dae-f5d4-45ba-993d-22fa61ca16e5',
                gameDocumentstate,
                displayLanguageContext.displayLanguageSelected.resources!,
                'game'
              ) || '% Total assets'
            ).replace('%', progress.total.toString())}
          </p>
        </Col>
        <Col xs={12}>
          <Button
            className={'k-button--gradient w-100'}
            themeColor={'primary'}
            onClick={handleNext}
            disabled={!isDone}>
            {generateTitleById(
              '079c039a-8207-4404-8eb6-558cf5d8477b',
              gameDocumentstate,
              displayLanguageContext.displayLanguageSelected.resources!,
              'game'
            ) || 'Next'}
          </Button>
        </Col>
      </Row>
    </Container>
  );
};
